import { useCallback, useMemo } from "react";
import { useWsmPlugin } from "./useWsmPlugin";
import WSMSensorConnectionState from "@WahooFitness/wsm-native/dist/esm/types/connection_state";
import { t } from "@lingui/macro";
import usePairedDevices from "./usePairedDevices";

const usePairedDevice = (appToken?: string) => {
  const { sensorManagerPlugin } = useWsmPlugin();

  const { devices, devicesLoading, devicesError, mutateDevices } = usePairedDevices();

  const device = useMemo(
    () => devices?.find((device) => device.appToken === appToken),
    [appToken, devices]
  );

  const reloadDevice = useCallback(() => {
    mutateDevices();
  }, [mutateDevices]);

  const connectionStateDisplay = useMemo(() => {
    switch (device?.connectionState) {
      case WSMSensorConnectionState.CONNECTED:
        return t`Connected`;
      case WSMSensorConnectionState.CONNECTING:
        return t`Connecting`;
      case WSMSensorConnectionState.DISCONNECTED:
        return t`Not connected`;
      case WSMSensorConnectionState.DISCONNECTING:
        return t`Disconnecting`;
    }
  }, [device?.connectionState]);

  const unpairDevice = useCallback(async () => {
    if (device) {
      await sensorManagerPlugin?.unpairFromDevice(device);
      mutateDevices();
    }
  }, [device, mutateDevices, sensorManagerPlugin]);

  const isDeviceConnected = useMemo(
    () => device?.connectionState === WSMSensorConnectionState.CONNECTED,
    [device?.connectionState]
  );

  const setDeviceName = useCallback(
    async (name: string) => {
      if (device) {
        await sensorManagerPlugin?.setDeviceName({ sensorId: device.sensorId, name });
      }
    },
    [device, sensorManagerPlugin]
  );

  return {
    device,
    deviceError: devicesError,
    deviceLoading: devicesLoading,
    reloadDevice,
    isDeviceConnected,
    connectionStateDisplay,
    unpairDevice,
    setDeviceName,
  };
};

export default usePairedDevice;
